<!-- @format -->

<template>
	<div class="component-upload-image">
		<el-upload
			multiple
			:action="uploadImgUrl"
			list-type="picture-card"
			:on-success="handleUploadSuccess"
			:before-upload="handleBeforeUpload"
			:limit="limit"
			:on-error="handleUploadError"
			:on-exceed="handleExceed"
			name="file"
			:on-remove="handleRemove"
			:show-file-list="true"
			:headers="headers"
			:file-list="fileList"
			:on-preview="handlePictureCardPreview"
			:class="{ hide: fileList.length >= limit }"
			:data="data"
		>
			<el-icon class="avatar-uploader-icon"><plus /></el-icon>
		</el-upload>
		<!-- 上传提示 -->
		<div v-if="showTip" class="el-upload__tip">
			请上传
			<template v-if="fileSize">
				大小不超过 <b style="color: #f56c6c">{{ fileSize }}MB</b>
			</template>
			<template v-if="fileType">
				格式为 <b style="color: #f56c6c">{{ fileType.join('/') }}</b>
			</template>
			的文件
		</div>

		<el-dialog v-model="dialogVisible" :close-on-click-modal="false" title="预览" width="800px" append-to-body>
			<img :src="dialogImageUrl" style="display: block; max-width: 100%; margin: 0 auto" />
		</el-dialog>
	</div>
</template>

<script setup>
	import { getFileUrlById, getEnvUrlByUrl, getToken } from '@power/runtime-vue3';
	import { PcModal as modal } from '@/components';

	const props = defineProps({
		modelValue: [Array, String, Object],
		// 图片数量限制
		limit: {
			type: Number,
			default: 5,
		},
		// 大小限制(MB)
		fileSize: {
			type: Number,
			default: 5,
		},
		// 文件类型, 例如['png', 'jpg', 'jpeg']
		fileType: {
			type: Array,
			default: () => ['png', 'jpg', 'jpeg'],
		},
		// 是否显示提示
		isShowTip: {
			type: Boolean,
			default: true,
		},
		data: {
			type: Object,
			default: () => {
				return { folderAlias: '', groupKey: '' };
			},
		},
	});

	const emit = defineEmits([]);
	const number = ref(0);
	const uploadList = ref([]);
	const dialogImageUrl = ref('');
	const dialogVisible = ref(false);
	const baseUrl = getEnvUrlByUrl('power-api/');
	const uploadImgUrl = ref(
		'V1' === window.pData.env.VITE_ENV_APP_BASE_API_VERSION
			? `${getEnvUrlByUrl('power-api/file/upload')}`
			: `${getEnvUrlByUrl('power-api/base/oss/file/upload')}`,
	); // 上传的图片服务器地址
	const headers = ref({ Authorization: `Bearer ${getToken()}` });
	const fileList = ref([]);
	const showTip = computed(() => props.isShowTip && (props.fileType || props.fileSize));

	watch(
		() => props.modelValue,
		(val) => {
			// console.log('props.modelValue', val);
			if (val) {
				// 首先将值转为数组
				const list = Array.isArray(val) ? val : props.modelValue.split(',');
				// 然后将数组转为对象数组
				fileList.value = list.map((item) => {
					if (typeof item === 'string') {
						item = { name: item, url: item };
					}
					return item;
				});
			} else {
				fileList.value = [];
				return [];
			}
		},
		{ deep: true, immediate: true },
	);

	// 删除图片
	function handleRemove(_file, _files) {
		emit('update:modelValue', listToArray(fileList.value));
	}

	// 上传成功回调
	async function handleUploadSuccess(res) {
		if ('V1' === window.pData.env.VITE_ENV_APP_BASE_API_VERSION) {
			uploadList.value.push({ name: res.data.name, url: res.data.url });
		} else {
			const file = await getFileUrlById(res.data.id);
			uploadList.value.push({ name: res.data.resourceName, url: file });
		}
		// console.log('uploadList.value.length', uploadList.value.length);
		if (uploadList.value.length === number.value) {
			// console.log('fileList.value', fileList.value);
			fileList.value = fileList.value
				.filter((f) => f.url !== undefined && f.url.indexOf('blob:') !== 0)
				.concat(uploadList.value);
			uploadList.value = [];
			// console.log('fileList.value', fileList.value);

			number.value = 0;
			emit('update:modelValue', listToArray(fileList.value));
			modal.closeLoading();
		}
	}

	// 上传前loading加载
	function handleBeforeUpload(file) {
		let isImg = false;
		if (props.fileType.length) {
			let fileExtension = '';
			if (file.name.lastIndexOf('.') > -1) {
				fileExtension = file.name.slice(file.name.lastIndexOf('.') + 1);
			}
			isImg = props.fileType.some((type) => {
				if (file.type.indexOf(type) > -1) return true;
				if (fileExtension && fileExtension.indexOf(type) > -1) return true;
				return false;
			});
		} else {
			isImg = file.type.indexOf('image') > -1;
		}
		if (!isImg) {
			modal.msgError(`文件格式不正确, 请上传${props.fileType.join('/')}图片格式文件!`);
			return false;
		}
		if (props.fileSize) {
			const isLt = file.size / 1024 / 1024 < props.fileSize;
			if (!isLt) {
				modal.msgError(`上传头像图片大小不能超过 ${props.fileSize} MB!`);
				return false;
			}
		}
		modal.loading('正在上传图片，请稍候...');
		number.value++;
	}

	// 文件个数超出
	function handleExceed() {
		modal.msgError(`上传文件数量不能超过 ${props.limit} 个!`);
	}

	// 上传失败
	function handleUploadError() {
		modal.msgError('上传图片失败');
		modal.closeLoading();
	}

	// 预览
	function handlePictureCardPreview(file) {
		dialogImageUrl.value = file.url;
		dialogVisible.value = true;
	}

	// 对象转成指定数组格式
	function listToArray(list) {
		return list.map((i) => i.url);
	}

	// 对象转成指定字符串分隔
	function listToString(list, separator) {
		let strs = '';
		separator = separator || ',';
		for (const i in list) {
			if (undefined !== list[i].url && list[i].url.indexOf('blob:') !== 0) {
				strs += list[i].url.replace(baseUrl, '') + separator;
			}
		}
		return strs != '' ? strs.substr(0, strs.length - 1) : '';
	}
	listToString();
</script>

<style scoped lang="scss">
	// .el-upload--picture-card 控制加号部分
	:deep(.hide .el-upload--picture-card) {
		display: none;
	}

	:deep(.el-upload--picture-card) {
		border-radius: 1px;
	}

	:deep(.el-upload-list--picture-card .el-upload-list__item) {
		border-radius: 1px;
	}
</style>
